import { forwardRef, useCallback, useMemo, useState } from 'react'
import { InputColor, Select, SelectValue, ToggleTabs } from '@dashboard/ui'
import { useGenerateCompanyPath } from '@/libs'
import { websiteLastQueryOpts } from '@/layouts/websites'
import { useQuery } from '@tanstack/react-query'
import { useTransformedTheme } from '../../hooks'
import { WebsiteRead } from '@/api/dashboard'
import { TransformedToken } from 'style-dictionary'
import { ColorSwatch } from '../ColorSwatch'
import { TransformedTheme } from '../../types'

interface FormTypeColorControlProps {
  value: string
  onChange: (value: string) => void
}

const getTokensByType = (
  theme: TransformedTheme,
  type: 'color',
): {
  token: TransformedToken
  path: string
}[] => {
  const result: { token: TransformedToken; path: string }[] = []

  const flattenObject = (obj: Record<string, unknown>, path: string[] = []) => {
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = [...path, key]

      if (typeof value === 'object' && value !== null) {
        if ('value' in value) {
          result.push({
            token: value as TransformedToken,
            path: currentPath.join('.'),
          })
        } else {
          flattenObject(value as Record<string, unknown>, currentPath)
        }
      }
    })
  }

  Object.entries(theme).forEach(([category, items]) => {
    flattenObject(items as Record<string, unknown>, [category])
  })

  return result.filter(token => token.token.type === type)
}

export const FormTypeColorControl = forwardRef<HTMLInputElement, FormTypeColorControlProps>(function (
  { value, onChange },
  ref,
) {
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: website } = useQuery(websiteLastQueryOpts(companyId, gameId))
  const { data: transformedTheme } = useTransformedTheme(gameId, website as WebsiteRead)

  const [tab, setTab] = useState<'link' | 'raw'>(value?.includes('{') ? 'link' : 'raw')

  const colorTokens = useMemo(
    () => (transformedTheme?.json ? getTokensByType(transformedTheme.json, 'color') : []),
    [transformedTheme],
  )

  const onChangeTab = useCallback(
    (value: string | number | null) => {
      setTab(value as 'link' | 'raw')
      onChange('')
    },
    [onChange],
  )

  const onChangeSelect = useCallback(
    (value: SelectValue) => {
      onChange(value as string)
    },
    [onChange],
  )

  const onChangeInput = useCallback(
    (value: string) => {
      onChange(value)
    },
    [onChange],
  )

  return (
    <div className="flex flex-col gap-2">
      <ToggleTabs
        tabs={[
          { children: 'Link', value: 'link' },
          { children: 'Value', value: 'raw' },
        ]}
        stretch={true}
        value={tab}
        onChange={onChangeTab}
      />

      {tab === 'link' && (
        <Select
          search={{
            fn: (query, item) => {
              return item.value?.toString().toLowerCase().includes(query.toLowerCase()) || false
            },
            placeholder: 'Search',
          }}
          items={colorTokens.map(item => ({
            children: item.token.name,
            value: `{${item.path}}`,
            icon: () => <ColorSwatch color={item.token.value} size="sm" />,
          }))}
          value={value}
          onChange={onChangeSelect}
        />
      )}

      {tab === 'raw' && <InputColor ref={ref} options={{ alpha: true }} value={value} onChange={onChangeInput} />}
    </div>
  )
})
